import { uniqueId } from "lodash";
import { useEffect, useState } from "react";
import Button, {
  ButtonTypes,
} from "../../../../common/components/button/button";
import Dropdown from "../../../../common/components/inputFields/dropdown/dropdown";
import TextField from "../../../../common/components/inputFields/textFields/textField";
import { useLoadHandler } from "../../../../common/customHooks/useLoadHandler";
import { FileApi } from "../../../../common/services/api/fileApi";
import { Color } from "../../../../common/style/colors";
import { CoverDetailModel } from "../../models/createCampaignModel";
import { ThemeModel } from "../../models/themeModel";
import AddPolicyPopup from "../addPolicyPopup/addPolicyPopup";
import CoverPreviewArea, {
  Position,
} from "../coverPreviewArea/coverPreviewArea";
import "./createCampaignCover.scss";

export default function CreateCampaignCover(props: {
  initialValue?: CoverDetailModel;
  onChange?: (e: CoverDetailModel) => void;
  themeData?: ThemeModel;
}) {
  const [coverDetail, setCoverDetail] = useState<CoverDetailModel>(
    props.initialValue ?? {
      buttonText: "Continue",
      buttonPosition: Position.BOTTOM_CENTER,
    }
  );
  const [showPolicyPopup, setShowPolicyPopup] = useState(false);
  const [desktopBackgroundId] = useState<string>(uniqueId());
  const [mobileBackgroundId] = useState<string>(uniqueId());

  const {
    loadingStatus: mobilepLoadingStatus,
    callFetchAsync: mobileCallFetchAsync,
  } = useLoadHandler(async (file: File) => {
    let result = await FileApi.uploadFile(file);
    setCoverDetail({
      ...coverDetail,
      mobileBackground: result,
    });
  });

  const {
    loadingStatus: desktopLoadingStatus,
    callFetchAsync: desktopCallFetchAsync,
  } = useLoadHandler(async (file: File) => {
    let result = await FileApi.uploadFile(file);
    setCoverDetail({
      ...coverDetail,
      desktopBackground: result,
    });
  });

  useEffect(() => {
    props.onChange?.(coverDetail);
  }, [coverDetail]);

  return (
    <>
      <div className="create-campaign-cover">
        <div className="create-campaign-cover-header">
          <div>
            <h3 className="create-campaign-title">Campaign cover</h3>
            <p className="create-campaign-description">
              This campaign cover will be the landing page of your created
              campaign!
            </p>
          </div>
          <Button
            type={ButtonTypes.PRIMARY}
            loadSpinColor={Color().primaryColor}
            text={"add policy"}
            onClick={() => setShowPolicyPopup(true)}
          />
        </div>
        <hr style={{ width: "100%" }} />
        <h4 style={{ textAlign: "center" }}>Campaign cover preview</h4>
        <div className="upload-cover-container">
          <Button
            type={ButtonTypes.OUTLINED}
            loadSpinColor={Color().primaryColor}
            text={"upload desktop BG"}
            onClick={() => {
              document.getElementById(desktopBackgroundId)?.click();
            }}
            loadingStatus={desktopLoadingStatus}
          />
          <Button
            type={ButtonTypes.OUTLINED}
            loadSpinColor={Color().primaryColor}
            text={"upload mobile BG"}
            onClick={() => {
              document.getElementById(mobileBackgroundId)?.click();
            }}
            loadingStatus={mobilepLoadingStatus}
          />
        </div>
        <div className="cover-preview">
          <CoverPreviewArea
            buttonColor={props.themeData?.themeColor?.value}
            buttonText={coverDetail.buttonText}
            fontFamily={props.themeData?.fontFamily}
            image={coverDetail.desktopBackground}
            description="Desktop"
            buttonPosition={coverDetail.buttonPosition}
            ratio="5/3"
            style={{ flex: 11, minWidth: "275px" }}
            infoText="Accept all types of image file, and optimal size is 500KB (1280*720)"
          />
          <CoverPreviewArea
            buttonColor={props.themeData?.themeColor?.value}
            buttonText={coverDetail.buttonText}
            fontFamily={props.themeData?.fontFamily}
            image={coverDetail.mobileBackground}
            description="Mobile"
            buttonPosition={coverDetail.buttonPosition}
            ratio="3/5"
            style={{ flex: 4, maxWidth: "200px" }}
            infoText="Accept all types of image file, and optimal size is 300KB (640*360)"
          />
        </div>
        <div className="cover-inputs-container">
          <div style={{ flex: 1 }}>
            <Dropdown
              initialValue={{
                value: coverDetail.buttonPosition,
                label: coverDetail.buttonPosition,
              }}
              options={Object.values(Position).map((e) => ({
                value: e,
                label: e,
              }))}
              onChange={(e) =>
                setCoverDetail({
                  ...coverDetail,
                  buttonPosition: e.value as Position,
                })
              }
            />
          </div>
          <div style={{ flex: 3 }}>
            <TextField
              initialValue={coverDetail.buttonText ?? ""}
              onChange={(e) =>
                setCoverDetail({
                  ...coverDetail,
                  buttonText: e,
                })
              }
            />
          </div>
        </div>
      </div>
      <input
        type="file"
        accept="image/*"
        id={desktopBackgroundId}
        style={{ display: "none" }}
        onChange={(e) => desktopCallFetchAsync(e.target.files?.[0])}
      />
      <input
        type="file"
        accept="image/*"
        id={mobileBackgroundId}
        style={{ display: "none" }}
        onChange={(e) => mobileCallFetchAsync(e.target.files?.[0])}
      />
      {showPolicyPopup && (
        <AddPolicyPopup
          initialValue={coverDetail.policy}
          onCancel={() => setShowPolicyPopup(false)}
          onConfirm={(e) => {
            setCoverDetail({
              ...coverDetail,
              policy: e,
            });
            setShowPolicyPopup(false);
          }}
        />
      )}
    </>
  );
}
